<template>
  <div class="tab-stats-container">

    <!-- Overview -->
    <template v-if="tabId == 'overview'">
      <section class="tab-stats">
        <div class="tab-stats__graph-wrap">
        <div class="tab-stats-column tab-stats-column--sidebar">
          <h2 class="tab-stats__title">{{ title }}</h2>

          <div class="tab-stats-block"
            v-if="overviewTotalSales"
          >
            <small class="tab-stats__small">Total Sales</small>
            <p class="tab-stats__text tab-stats__text--30">{{ overviewTotalSales.amount | currency }}</p>
            <p class="tab-stats__text tab-stats__text--11"
              v-if="overviewDifference"
              :class="{ 'tab-stats__text--positive': overviewDifference.difference >= 0, 'tab-stats__text--negative': overviewDifference.difference < 0 }"
            >{{ overviewDifference.direction }}{{ overviewDifference.amount | currency }} ({{ overviewDifference.difference }}%)</p>
          </div>

          <!-- Hide for now
          <div class="tab-stats-block tab-stats-block--25">
            <small class="tab-stats__small">Conversion Rate</small>
            <p class="tab-stats__text tab-stats__text--19">3.2%</p>
          </div>
          -->

          <div class="tab-stats-block tab-stats-block--note" v-if="overviewSalesPercentage !== null && overviewSalesAmount !== null && overviewSalesAmountAll !== null">
            <div class="tab-stats-highlight">
              <h3 class="tab-stats-highlight__title" v-if="overviewSalesPercentage >= 80">Rockstar Product</h3>
              <div class="tab-stats-highlight__info">
                <p>This product accounts for {{ overviewSalesPercentage.toFixed(2) }}% of the total sales ({{ overviewSalesAmount | currency }} of {{ overviewSalesAmountAll | currency }}) in your store during the selected date range.</p>
              </div>
            </div>
          </div>
        </div>

        <div class="tab-stats-column tab-stats-column--main">
          <div class="tab-stats-block">
            <dl
              class="tab-stats__date-range"
              v-if="$parent.$parent.filterDateRange"
            >
              <dt></dt>
              <dd>{{ dateFormat($parent.$parent.filterDateRange.start, 'MMM D, YYYY') }} - {{ dateFormat($parent.$parent.filterDateRange.end, 'MMM D, YYYY') }}</dd>
              <dt></dt>
              <dd>{{ dateFormat($parent.previousDateRange.start, 'MMM D, YYYY') }} - {{ dateFormat($parent.previousDateRange.end, 'MMM D, YYYY') }}</dd>
            </dl>
          </div>

          <line-chart
            v-if="overviewSalesHistory"
            :chartData="overviewSalesHistory"
            :options="{responsive: false, maintainAspectRatio: false}"
            :width="729"
            :height="240"
          />
        </div>
        </div>

        <section class="tab-stats-block">
          <div class="tab-stats__bubble-block">
            <StatBubblesBlock
              :stats="statsGroupOne"
              title="Summary"
              :dateRange="`${dateFormat($parent.$parent.filterDateRange.start, 'MMM D, YYYY')} - ${dateFormat($parent.$parent.filterDateRange.end, 'MMM D, YYYY')}`"
            />
          </div>

          <div class="tab-stats__bubble-block">
            <StatBubblesBlock
              :stats="statsGroupTwo"
              title="Details"
              :dateRange="`${dateFormat($parent.$parent.filterDateRange.start, 'MMM D, YYYY')} - ${dateFormat($parent.$parent.filterDateRange.end, 'MMM D, YYYY')}`"
            />
          </div>
        </section>
      </section>

    </template>

    <!-- Sales -->
    <template v-if="tabId == 'sales'">
      <section class="tab-stats">
        <h2 class="tab-stats__title">{{ title }}</h2>

        <!-- Sales Orders -->
        <div class="tab-stats-columns-alt tab-stats-columns-alt--sales-orders">
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Orders</h3>

            <div class="tab-stats-block tab-stats-block--25" v-if="salesOrdersSummary !== null">
              <div class="tab-stats__info">
                <p><strong>{{ salesOrdersSummary.toFixed(2) }}%</strong> of all orders during the selected period had this product.</p>
              </div>
            </div>

          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
            <div class="tab-stats-dashboard" v-if="salesOrders && salesOrderVelocity">
              <ul class="dashboard-tile__list">
                  <dashboard-tile
                    v-for="(dashboard, key) in salesOrders"
                    :amount="number(dashboard.amount)"
                    :key="key"
                    :metric="false"
                    :name="dashboard.name"
                    :previous-amount="0"
                    :type="dashboard.type"
                  ></dashboard-tile>

                <dashboard-tile
                  :amount="salesOrderVelocity.amount"
                  :metric="false"
                  :name="salesOrderVelocity.name"
                  :previous-amount="0"
                  type="string"
                ></dashboard-tile>
              </ul>
            </div>
          </div>
        </div>

        <!-- Sales Customers -->
        <div class="tab-stats-columns-alt tab-stats-columns-alt--sales-customers">
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Customers</h3>

            <div class="tab-stats-block tab-stats-block--25" v-if="salesCustomersSummary !== null">
              <div class="tab-stats__info">
                <p><strong>{{ salesCustomersSummary.toFixed(2) }}%</strong> of your customers have purchased this product during the selected date range.</p>
              </div>
            </div>
          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
            <div class="tab-stats-dashboard" v-if="salesCustomers">
              <ul class="dashboard-tile__list">
                <template v-for="(dashboard, key) in salesCustomers">
                  <dashboard-tile
                    :amount="dashboard.amount"
                    :key="key"
                    :metric="false"
                    :name="dashboard.name"
                    :previous-amount="0"
                    type="int"
                  ></dashboard-tile>
                </template>
              </ul>
          <ul class="tab-stats-accordion" v-if="(salesCustomersNew && salesCustomersNew.length > 0) || (salesCustomersReturning && salesCustomersReturning.length > 0)">
            <li class="tab-stats-accordion__item" v-if="salesCustomersNew && salesCustomersNew.length > 0">
              <button type="button" class="tab-stats-accordion__button"
                @click="slideToggle('.tab-stats-accordion-columns', $event); iScrollUpdate()"
              >New Customers</button>

              <ul class="tab-stats-accordion-columns">

                <li class="tab-stats-accordion-column tab-stats-accordion-column--main">
                  <div class="tab-table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Amount Spent</th>
                          <th>Recent Purchase</th>
                          <th>First Purchase</th>
                          <th v-if="tableMenu" class="tab-stats-thead-menu">Menu</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(item, key) in salesCustomersNewPages"
                          :key="key"
                        >
                          <td>{{ item.name }}</td>
                          <td>{{ item.total_spent | currency }}</td>
                          <td>{{ item.first_order_date ? dateFormat(item.first_order_date, 'MM-DD-YYYY') : '--' }}</td>
                          <td>{{ item.last_order_date ? dateFormat(item.last_order_date, 'MM-DD-YYYY') : '--' }}</td>
                          <td class="tab-stats-table-menu-container"
                            v-if="tableMenu"
                          >
                            <button type="button" class="tab-stats-table-menu__button icon--more-menu"
                              @click="toggleMenu"
                            >Menu</button>

                            <div class="tab-stats-table-menu">
                              <ul class="tab-stats-table-menu__list">
                                <li class="tab-stats-table-menu__item">
                                  <a class="tab-stats-table-menu__link">Add to Mailchimp</a>
                                </li>
                                <li class="tab-stats-table-menu__item">
                                  <a class="tab-stats-table-menu__link">Send Offer</a>
                                </li>
                                <li class="tab-stats-table-menu__item">
                                  <a class="tab-stats-table-menu__link">Message</a>
                                </li>
                                <li class="tab-stats-table-menu__item">
                                  <a class="tab-stats-table-menu__link">Delete</a>
                                </li>
                                <li class="tab-stats-table-menu__item tab-stats-table-menu__item--unlock">
                                  <a class="tab-stats-table-menu__link"><u>Unlock</u> these actions</a>
                                </li>
                              </ul>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div class="pagination" v-if="salesCustomersNew.length > itemsPerPage">
                      <span :class="`pagination__link pagination__prev pagination__${hasPrev('salesCustomersNew') ? 'active' : 'inactive' }`" @click="prevPage('salesCustomersNew')">&lt; Previous</span>
                      <span class="pagination__pages">Showing {{ pageRange('salesCustomersNew')[0] }} - {{ pageRange('salesCustomersNew')[1] }} of {{ salesCustomersNew.length }}</span>
                      <span :class="`pagination__link pagination__next pagination__${hasNext('salesCustomersNew') ? 'active' : 'inactive' }`" @click="nextPage('salesCustomersNew')">Next &gt;</span>
                    </div>
                  </div>
                </li>
              </ul>
            </li>

            <li class="tab-stats-accordion__item" v-if="salesCustomersReturning && salesCustomersReturning.length > 0">
              <button type="button" class="tab-stats-accordion__button"
                @click="slideToggle('.tab-stats-accordion-columns', $event); iScrollUpdate()"
              >Returning Customers</button>

              <ul class="tab-stats-accordion-columns">


                <li class="tab-stats-accordion-column tab-stats-accordion-column--main">
                  <div class="tab-table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Amount Spent</th>
                          <th>Recent Purchase</th>
                          <th>First Purchase</th>
                          <th v-if="tableMenu" class="tab-stats-thead-menu">Menu</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(item, key) in salesCustomersReturningPages"
                          :key="key"
                        >
                          <td>{{ item.name }}</td>
                          <td>{{ item.total_spent | currency }}</td>
                          <td>{{ item.first_order_date ? dateFormat(item.first_order_date, 'MM-DD-YYYY') : '--' }}</td>
                          <td>{{ item.last_order_date ? dateFormat(item.last_order_date, 'MM-DD-YYYY') : '--' }}</td>
                          <td class="tab-stats-table-menu-container"
                            v-if="tableMenu"
                          >
                            <button type="button" class="tab-stats-table-menu__button icon--more-menu"
                              @click="toggleMenu"
                            >Menu</button>

                            <div class="tab-stats-table-menu">
                              <ul class="tab-stats-table-menu__list">
                                <li class="tab-stats-table-menu__item">
                                  <a class="tab-stats-table-menu__link">Add to Mailchimp</a>
                                </li>
                                <li class="tab-stats-table-menu__item">
                                  <a class="tab-stats-table-menu__link">Send Offer</a>
                                </li>
                                <li class="tab-stats-table-menu__item">
                                  <a class="tab-stats-table-menu__link">Message</a>
                                </li>
                                <li class="tab-stats-table-menu__item">
                                  <a class="tab-stats-table-menu__link">Delete</a>
                                </li>
                                <li class="tab-stats-table-menu__item tab-stats-table-menu__item--unlock">
                                  <a class="tab-stats-table-menu__link"><u>Unlock</u> these actions</a>
                                </li>
                              </ul>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div class="pagination" v-if="salesCustomersReturning.length > itemsPerPage">
                      <span :class="`pagination__link pagination__prev pagination__${hasPrev('salesCustomersReturning') ? 'active' : 'inactive' }`" @click="prevPage('salesCustomersReturning')">&lt; Previous</span>
                      <span class="pagination__pages">Showing {{ pageRange('salesCustomersReturning')[0] }} - {{ pageRange('salesCustomersReturning')[1] }} of {{ salesCustomersReturning.length }}</span>
                      <span :class="`pagination__link pagination__next pagination__${hasNext('salesCustomersReturning') ? 'active' : 'inactive' }`" @click="nextPage('salesCustomersReturning')">Next &gt;</span>
                    </div>
                  </div>
                </li>
              </ul>
            </li>

            <li class="tab-stats-accordion__item" v-if="salesCustomersTop && salesCustomersTop.length > 0">
              <button type="button" class="tab-stats-accordion__button"
                @click="slideToggle('.tab-stats-accordion-columns', $event); iScrollUpdate()"
              >Top Customers</button>
                  <ul class="tab-stats-accordion-columns">
                    <li class="tab-stats-accordion-column tab-stats-accordion-column--main">
                      <div class="tab-table-container">
                        <table>
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Amount Spent</th>
                              <th>Recent Purchase</th>
                              <th>First Purchase</th>
                              <th v-if="tableMenu" class="tab-stats-thead-menu">Menu</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(item, key) in salesCustomersTop.slice(0, 10)"
                              :key="key"
                            >
                              <td>{{ item.name }}</td>
                              <td>{{ item.total_spent | currency }}</td>
                              <td>{{ item.first_order_date ? dateFormat(item.first_order_date, 'MM-DD-YYYY') : '--' }}</td>
                              <td>{{ item.last_order_date ? dateFormat(item.last_order_date, 'MM-DD-YYYY') : '--' }}</td>
                              <td class="tab-stats-table-menu-container"
                                v-if="tableMenu"
                              >
                                <button type="button" class="tab-stats-table-menu__button icon--more-menu"
                                  @click="toggleMenu"
                                >Menu</button>

                                <div class="tab-stats-table-menu">
                                  <ul class="tab-stats-table-menu__list">
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Add to Mailchimp</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Send Offer</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Message</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Delete</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item tab-stats-table-menu__item--unlock">
                                      <a class="tab-stats-table-menu__link"><u>Unlock</u> these actions</a>
                                    </li>
                                  </ul>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Sales Discounts -->
        <div class="tab-stats-columns-alt tab-stats-columns-alt--sales-discounts">
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Discounts</h3>

            <div class="tab-stats-block tab-stats-block--25" v-if="salesDiscountsSummary !== null">
              <div class="tab-stats__info">
                <p><strong>{{ salesDiscountsSummary.toFixed(2) }}%</strong> of all discounts taken during the selected date range contained this product.</p>
              </div>
            </div>
          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
            <div class="tab-stats-dashboard" v-if="salesDiscounts && salesDiscountsCount">
              <ul class="dashboard-tile__list">
                <dashboard-tile
                  :amount="number(salesDiscountsCount.amount)"
                  :metric="false"
                  :name="salesDiscountsCount.name"
                  :previous-amount="0"
                  type="string"
                ></dashboard-tile>

                <template v-for="(dashboard, key) in salesDiscounts">
                  <dashboard-tile
                    :amount="number(dashboard.amount)"
                    :key="key"
                    :metric="false"
                    :name="dashboard.name"
                    :previous-amount="number(dashboard.previous_amount)"
                    :type="dashboard.type"
                  ></dashboard-tile>
                </template>
              </ul>
              <ul class="tab-stats-accordion" v-if="salesDiscountsCustomers && salesDiscountsCustomers.length > 0">
                <li class="tab-stats-accordion__item">
                  <button type="button" class="tab-stats-accordion__button"
                    @click="slideToggle('.tab-stats-accordion-columns', $event); iScrollUpdate()"
                  >Customers Who Received Discounts</button>

                  <ul class="tab-stats-accordion-columns">
                    <li class="tab-stats-accordion-column tab-stats-accordion-column--main">
                      <div class="tab-table-container">
                        <table>
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Gross Amount</th>
                              <th>Discounted Amount</th>
                              <th>Net Amount</th>
                              <th v-if="tableMenu" class="tab-stats-thead-menu">Menu</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(item, key) in salesDiscountsCustomersPages"
                              :key="key"
                            >
                              <td>{{ item.name }}</td>
                              <td>{{ item.sales.price | currency }}</td>
                              <td>-{{ item.sales.total_discount | currency }}</td>
                              <td>{{ item.sales.net_price | currency }}</td>
                              <td class="tab-stats-table-menu-container"
                                v-if="tableMenu"
                              >
                                <button type="button" class="tab-stats-table-menu__button icon--more-menu"
                                  @click="toggleMenu"
                                >Menu</button>

                                <div class="tab-stats-table-menu">
                                  <ul class="tab-stats-table-menu__list">
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Add to Mailchimp</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Send Offer</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Message</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Delete</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item tab-stats-table-menu__item--unlock">
                                      <a class="tab-stats-table-menu__link"><u>Unlock</u> these actions</a>
                                    </li>
                                  </ul>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <div class="pagination" v-if="salesDiscountsCustomers.length > itemsPerPage">
                          <span :class="`pagination__link pagination__prev pagination__${hasPrev('salesDiscountsCustomers') ? 'active' : 'inactive' }`" @click="prevPage('salesDiscountsCustomers')">&lt; Previous</span>
                          <span class="pagination__pages">Showing {{ pageRange('salesDiscountsCustomers')[0] }} - {{ pageRange('salesDiscountsCustomers')[1] }} of {{ salesDiscountsCustomers.length }}</span>
                          <span :class="`pagination__link pagination__next pagination__${hasNext('salesDiscountsCustomers') ? 'active' : 'inactive' }`" @click="nextPage('salesDiscountsCustomers')">Next &gt;</span>
                        </div>
                      </div>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Sales Returns -->
        <div class="tab-stats-columns-alt tab-stats-columns-alt--sales-returns">
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Returns</h3>

            <div class="tab-stats-block tab-stats-block--25" v-if="salesReturnsSummary !== null">
              <div class="tab-stats__info">
                <p><strong>{{ salesReturnsSummary.toFixed(2) }}%</strong> of all discounts taken during the selected date range contained this product.</p>
              </div>
            </div>
          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
            <div class="tab-stats-dashboard" v-if="salesReturns">
              <ul class="dashboard-tile__list">
                <template v-for="(dashboard, key) in salesReturns">
                  <dashboard-tile
                    :amount="number(dashboard.amount)"
                    :key="key"
                    :metric="false"
                    :name="dashboard.name"
                    :previous-amount="number(dashboard.previous_amount)"
                    :type="dashboard.type"
                  ></dashboard-tile>
                </template>
              </ul>
              <ul class="tab-stats-accordion" v-if="salesReturnsCustomers && salesReturnsCustomers.length > 0">
                <li class="tab-stats-accordion__item">
                  <button type="button" class="tab-stats-accordion__button"
                    @click="slideToggle('.tab-stats-accordion-columns', $event); iScrollUpdate()"
                  >Customers Who Returned this Product</button>

                  <ul class="tab-stats-accordion-columns">
                    <li class="tab-stats-accordion-column tab-stats-accordion-column--main">
                      <div class="tab-table-container">
                        <table>
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Date</th>
                              <th>Amount Refunded</th>
                              <th v-if="tableMenu" class="tab-stats-thead-menu">Menu</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(item, key) in salesReturnsCustomers"
                              :key="key"
                            >
                              <td>{{ item.name }}</td>
                              <td>{{ item.last_order_date ? dateFormat(item.last_order_date, 'MM-DD-YYYY') : '--' }}</td>
                              <td>{{ item.sales.refund | currency }}</td>
                              <td class="tab-stats-table-menu-container"
                                v-if="tableMenu"
                              >
                                <button type="button" class="tab-stats-table-menu__button icon--more-menu"
                                  @click="toggleMenu"
                                >Menu</button>

                                <div class="tab-stats-table-menu">
                                  <ul class="tab-stats-table-menu__list">
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Add to Mailchimp</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Send Offer</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Message</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Delete</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item tab-stats-table-menu__item--unlock">
                                      <a class="tab-stats-table-menu__link"><u>Unlock</u> these actions</a>
                                    </li>
                                  </ul>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Sales Inventory -->
        <div class="tab-stats-columns-alt tab-stats-columns-alt--sales-inventory">
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Inventory</h3>

            <div class="tab-stats-block tab-stats-block--25" v-if="salesInventorySummary !== null">
              <div class="tab-stats__info">
                <p>This product has sold out <strong>{{ salesInventorySummary }}</strong> time{{ salesInventorySummary > 1 ? 's' : '' }} in the selected period.</p>
              </div>
            </div>
          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
            <div class="tab-stats-dashboard" v-if="salesInventoryUnits || salesInventorySellThrough || salesInventoryOrdered">
              <ul class="dashboard-tile__list">
                <template v-if="salesInventoryOrdered">
                  <dashboard-tile
                    :amount="number(salesInventoryOrdered.amount)"
                    :metric="false"
                    :name="salesInventoryOrdered.name"
                    :previous-amount="0"
                    help="Total units ordered for variants over selected date range"
                    type="int"
                  ></dashboard-tile>
                </template>

                <template v-if="salesInventoryUnits">
                  <dashboard-tile
                    :amount="number(salesInventoryUnits.amount)"
                    :metric="false"
                    :name="salesInventoryUnits.name"
                    :previous-amount="0"
                    help="Total quantity for variants over selected date range"
                    type="int"
                  ></dashboard-tile>
                </template>

                <template v-if="salesInventorySellThrough">
                  <dashboard-tile
                    :amount="salesInventorySellThrough.amount"
                    :metric="false"
                    :name="salesInventorySellThrough.name"
                    :previous-amount="0"
                    type="string"
                  ></dashboard-tile>
                </template>
              </ul>
              <ul class="tab-stats-accordion" v-if="salesInventoryVariants && salesInventoryVariants.length > 0 || salesInventorySoldOut && salesInventorySoldOut.length > 0">
                <li class="tab-stats-accordion__item" v-if="salesInventoryVariants && salesInventoryVariants.length > 0">
                  <button type="button" class="tab-stats-accordion__button"
                    @click="slideToggle('.tab-stats-accordion-columns', $event); iScrollUpdate()"
                  >View Variants</button>

                  <ul class="tab-stats-accordion-columns">
                    <!--- Variants Tab -->
                    <li class="tab-stats-accordion-column tab-stats-accordion-column--main">
                      <div class="tab-table-container">
                        <table class="variant-inventory">
                          <thead>
                            <tr>
                              <th>Variant Title</th>
                              <th>Variant SKU</th>
                              <th>Current Stock</th>
                              <th title="Total units stocked for this item over the selected date range">Total Units Stocked</th>
                              <th>Total Units Sold</th>
                              <th>Sell Through Rate</th>
                              <th v-if="tableMenu" class="tab-stats-thead-menu">Menu</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(item, key) in salesInventoryVariants"
                              :key="key"
                              :class="`variant-inventory__${item.deleted ? 'deleted' : 'exists'}`"
                            >
                              <td class="variant-inventory__title" :title="`${item.deleted ? 'Variant has been deleted from shop' : ''}`">{{ item.title }}</td>
                              <td class="variant-inventory__sku" :title="`${item.deleted ? 'Variant has been deleted from shop' : ''}`">{{ item.sku }}</td>
                              <td class="variant-inventory__current">{{ item.inventory }}</td>
                              <td class="variant-inventory__stocked">{{ unitsStocked(item.id) }}</td>
                              <td class="variant-inventory__sold">{{ unitsSold(item.id) }}</td>
                              <td class="variant-inventory__sellthrough">{{ variantSellThroughRate(item.id) }}%</td>
                              <td class="tab-stats-table-menu-container"
                                v-if="tableMenu"
                              >
                                <button type="button" class="tab-stats-table-menu__button icon--more-menu"
                                  @click="toggleMenu"
                                >Menu</button>

                                <div class="tab-stats-table-menu">
                                  <ul class="tab-stats-table-menu__list">
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Add to Mailchimp</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Send Offer</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Message</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Delete</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item tab-stats-table-menu__item--unlock">
                                      <a class="tab-stats-table-menu__link"><u>Unlock</u> these actions</a>
                                    </li>
                                  </ul>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </li>
                  </ul>
                </li>

                <li class="tab-stats-accordion__item" v-if="salesInventorySoldOut && salesInventorySoldOut.length > 0">
                  <button type="button" class="tab-stats-accordion__button"
                    @click="slideToggle('.tab-stats-accordion-columns', $event); iScrollUpdate()"
                  >Sold Out Dates</button>

                  <ul class="tab-stats-accordion-columns">

                    <li class="tab-stats-accordion-column tab-stats-accordion-column--main">
                      <div class="tab-table-container">
                        <table class="variant-soldout">
                          <thead>
                            <tr>
                              <th>Variant Title</th>
                              <th>Variant SKU</th>
                              <th>Sold Out Date</th>
                              <th v-if="tableMenu" class="tab-stats-thead-menu">Menu</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(item, key) in salesInventorySoldOut"
                              :key="key"
                              :class="`variant-soldout__${variantDeleted(item.variant_id) ? 'deleted' : 'exists'}`"
                            >
                              <td class="variant-soldout__title" :title="`${variantDeleted(item.variant_id) ? 'Variant deleted from Shop' : ''}`">{{ item.title || 'Deleted Variant' }}</td>
                              <td class="variant-soldout__sku" :title="`${variantDeleted(item.variant_id) ? 'Variant deleted from Shop' : ''}`">{{ item.sku || 'Deleted Variant' }}</tdclas>
                              <td class="variant-soldout__date">{{ item.date ? dateFormat(item.date, 'MM-DD-YYYY') : '--' }}</td>
                              <td class="tab-stats-table-menu-container"
                                v-if="tableMenu"
                              >
                                <button type="button" class="tab-stats-table-menu__button icon--more-menu"
                                  @click="toggleMenu"
                                >Menu</button>

                                <div class="tab-stats-table-menu">
                                  <ul class="tab-stats-table-menu__list">
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Add to Mailchimp</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Send Offer</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Message</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item">
                                      <a class="tab-stats-table-menu__link">Delete</a>
                                    </li>
                                    <li class="tab-stats-table-menu__item tab-stats-table-menu__item--unlock">
                                      <a class="tab-stats-table-menu__link"><u>Unlock</u> these actions</a>
                                    </li>
                                  </ul>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Sales Performance -->
        <div class="tab-stats-columns-alt tab-stats-columns-alt--sales-performance">
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Performance</h3>
          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
            <div class="tab-stats-dashboard" v-if="salesPerformance">
              <ul class="dashboard-tile__list">
                <template v-for="(dashboard, key) in salesPerformance">
                  <dashboard-tile
                    :amount="dashboard.amount"
                    :key="key"
                    :metric="false"
                    :name="dashboard.name"
                    :previous-amount="0"
                    type="string"
                  ></dashboard-tile>
                </template>
              </ul>
              <ul class="tab-stats-accordion" v-if="(salesPerformanceBest) || (salesPerformanceWorst)">
                <li class="tab-stats-accordion__item">
                  <button type="button" class="tab-stats-accordion__button"
                    @click="slideToggle('.tab-stats-accordion-columns', $event); iScrollUpdate()"
                  >Best Performing Variant</button>

                  <ul class="tab-stats-accordion-columns">
                    <li class="tab-stats-accordion-column tab-stats-accordion-column--main">
                      <ProductDetailVariants
                      :productId="id"
                      tabActiveId="overview"
                      :propsProduct="product"
                      :propsProductVariants="product.variants"
                      :topWorst="true"
                      :tab="true"
                      v-if="product && product.variants"
                      variantsViewStyle="tab-best"
                    /></li>
                  </ul>
                </li>

                <li class="tab-stats-accordion__item">
                  <button type="button" class="tab-stats-accordion__button"
                    @click="slideToggle('.tab-stats-accordion-columns', $event); iScrollUpdate()"
                  >Worst Performing Variant</button>

                  <ul class="tab-stats-accordion-columns">
                    <li class="tab-stats-accordion-column tab-stats-accordion-column--main">
                      <ProductDetailVariants
                      :productId="id"
                      tabActiveId="overview"
                      :propsProduct="product"
                      :propsProductVariants="product.variants"
                      :topWorst="true"
                      :tab="true"
                      variantsViewStyle="tab-worst"
                    /></li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Sales Affinity -->
        <div class="tab-stats-columns-alt tab-stats-columns-alt--sales-affinity" v-if="salesAffinity && salesAffinity.length > 0">
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Affinity</h3>

            <div class="tab-stats-block tab-stats-block--25">
              <div class="tab-stats__info">
                <p>This product is frequently bought with these other products.</p>
              </div>
            </div>

            <!--
            <div class="tab-stats-block tab-stats-block--25">
              <a class="tab-stats__cta button-outline">Create a product bundle</a>
            </div>
            -->
          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
            <article class="products">
              <ul class="products__list">
                <template v-for="(product, key) in salesAffinity">
                  <product-tile
                    :hot="false"
                    :hover="false"
                    :id="product.id"
                    :image="product.image"
                    :inventory="product.inventory"
                    :key="key"
                    :menu="false"
                    :platformAdminUrl="product._links.platform_admin_url.href"
                    :platformUrl="product._links.platform_url.href"
                    :price="product.price"
                    :product="product"
                    :published="product.published"
                    :title="product.title"
                    :totalSales="product.sales.total_price"
                    :totalVariants="product.variant_list.length"
                    :unitsSold="product.sales.quantity"
                    @loadProductDetail="$parent.$parent.updateProductRoute"
                  ></product-tile>
                </template>
              </ul>
            </article>
          </div>
        </div>
      </section>
    </template>

    <!-- Checkout -->
    <template v-if="tabId == 'checkout'">
      <section class="tab-stats">
        <h2 class="tab-stats__title">{{ title }}</h2>

        <!-- Checkout Cart -->
        <div class="tab-stats-columns-alt tab-stats-columns-alt--checkout-cart">
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Cart</h3>

            <div class="tab-stats-block tab-stats-block--25" v-if="checkoutCartPercentage !== null">
              <div class="tab-stats__info">
                <p>This product accounts for <strong>{{ checkoutCartPercentage.toFixed(2) }}</strong> of all products customers added to their carts during the selected period.</p>
              </div>
            </div>
          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
            <div class="tab-stats-dashboard" v-if="checkoutCart">
              <ul class="dashboard-tile__list">
                <template v-for="(dashboard, key) in checkoutCart">
                  <dashboard-tile
                    :amount="dashboard.amount"
                    :key="key"
                    :metric="false"
                    :name="dashboard.name"
                    :previous-amount="0"
                    :type="dashboard.type"
                  ></dashboard-tile>
                </template>
              </ul>
            </div>
          </div>
        </div>

        <!-- Checkout Recovery -->
        <div class="tab-stats-columns-alt tab-stats-columns-alt--checkout-recovery">
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Checkout Recovery</h3>

            <div class="tab-stats-block tab-stats-block--25" v-if="checkoutCartVisitors !== null">
              <div class="tab-stats__info">
                <p>In the (selected period), <strong>{{ checkoutCartVisitors }}</strong> visitors added this product to their cart.</p>
              </div>
            </div>
          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
            <div class="tab-stats-dashboard" v-if="checkoutRecovery">
              <ul class="dashboard-tile__list">
                <template v-for="(dashboard, key) in checkoutRecovery">
                  <dashboard-tile
                    :amount="dashboard.amount"
                    :key="key"
                    :metric="false"
                    :name="dashboard.name"
                    :previous-amount="0"
                    :type="dashboard.type"
                  ></dashboard-tile>
                </template>
              </ul>
            </div>
          </div>
        </div>

        <!-- Checkout Flow -->
        <div class="tab-stats-columns-alt tab-stats-columns-alt--checkout-flow">
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Checkout Flow</h3>

            <div class="tab-stats-block tab-stats-block--25" v-if="checkoutFlowPercentage !== null">
              <div class="tab-stats__info">
                <p><strong>{{ checkoutFlowPercentage.toFixed(2) }}</strong> of customers who added this product to their cart completed their checkout.</p>
              </div>
            </div>
          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
            <div class="checkout-flow-chart" v-if="checkoutFlowChart">
              <ul class="checkout-flow-chart__list">
                <li class="checkout-flow-chart__item">
                  <ul class="checkout-flow-chart-cells">
                    <li class="checkout-flow-chart-cells__cell"></li>
                    <li class="checkout-flow-chart-cells__cell"></li>
                    <li class="checkout-flow-chart-cells__cell"></li>
                    <li class="checkout-flow-chart-cells__cell">
                      <span class="checkout-flow-chat__marker">{{ checkoutFlowChart.added_percentage }}</span>
                    </li>
                    <li class="checkout-flow-chart-cells__bar" :style="{ height: checkoutFlowChart.added > 0 ? '100%' : '0%' }"></li>
                  </ul>
                  <div class="checkout-flow-chart-info">
                    <p class="checkout-flow-chart__title">Added to Cart</p>
                    <p class="checkout-flow-chart__total">{{ checkoutFlowChart.added }}</p>
                  </div>
                </li>

                <li class="checkout-flow-chart__item">
                  <ul class="checkout-flow-chart-cells">
                    <li class="checkout-flow-chart-cells__cell"></li>
                    <li class="checkout-flow-chart-cells__cell"></li>
                    <li class="checkout-flow-chart-cells__cell"></li>
                    <li class="checkout-flow-chart-cells__cell">
                      <span class="checkout-flow-chat__marker">{{ checkoutFlowChart.started_percentage }}</span>
                    </li>
                    <li class="checkout-flow-chart-cells__bar" :style="{ height: checkoutFlowChart.added_percentage }"></li>
                  </ul>
                  <div class="checkout-flow-chart-info">
                    <p class="checkout-flow-chart__title">Started Checkout</p>
                    <p class="checkout-flow-chart__total">{{ checkoutFlowChart.started }}</p>
                  </div>
                </li>

                <li class="checkout-flow-chart__item">
                  <ul class="checkout-flow-chart-cells">
                    <li class="checkout-flow-chart-cells__cell"></li>
                    <li class="checkout-flow-chart-cells__cell"></li>
                    <li class="checkout-flow-chart-cells__cell"></li>
                    <li class="checkout-flow-chart-cells__cell">
                      <span class="checkout-flow-chat__dot">{{ checkoutFlowChart.purchased_percentage }}</span>
                    </li>
                    <li class="checkout-flow-chart-cells__bar" :style="{ height: checkoutFlowChart.purchased_percentage }"></li>
                  </ul>
                  <div class="checkout-flow-chart-info">
                    <p class="checkout-flow-chart__title">Purchased</p>
                    <p class="checkout-flow-chart__total">{{ checkoutFlowChart.purchased }}</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </template>

    <!-- Traffic -->
    <template v-if="tabId == 'traffic'">
      <section class="tab-stats">
        <h2 class="tab-stats__title">{{ title }}</h2>

        <AnalyticsConnect />

        <!-- Traffic Visitors -->
        <div class="tab-stats-columns-alt tab-stats-columns-alt--traffic-visitors">
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Visitors</h3>

            <div class="tab-stats-block tab-stats-block--25" v-if="trafficVisitorsSummary !== null">
              <div class="tab-stats__info">
                <p><strong>{{ trafficVisitorsSummary }}</strong> of all site visitors viewed this product during the selected date range.</p>
              </div>
            </div>
          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
            <div class="tab-stats-dashboard" v-if="trafficVisitors">
              <ul class="dashboard-tile__list">
                <template v-for="(dashboard, key) in trafficVisitors">
                  <dashboard-tile
                    :amount="dashboard.amount"
                    :key="key"
                    :metric="false"
                    :name="dashboard.name"
                    :previous-amount="0"
                    :type="dashboard.type"
                  ></dashboard-tile>
                </template>
              </ul>
            </div>
          </div>
        </div>

        <!-- Traffic Devices -->
        <div class="tab-stats-columns-alt tab-stats-columns-alt--traffic-devices">
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Devices</h3>

            <div class="tab-stats-block tab-stats-block--25" v-if="trafficDevicesSummary !== null">
              <div class="tab-stats__info">
                <p>Visitors on <strong>{{ trafficDevicesSummary }}</strong> purchased this product the most during the selected date range.</p>
              </div>
            </div>

            <!--
            <div class="tab-stats-block tab-stats-block--25">
              <a href="#" class="tab-stats__cta button-outline">See all device types</a>
            </div>
            -->
          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
            <div class="tab-table-container" v-if="trafficDevices">
              <table>
                <thead>
                  <tr>
                    <th>Device Type</th>
                    <th>Visitors</th>
                    <th>% of Visitors</th>
                    <th>Sales</th>
                    <th>Conversion Rate</th>
                    <th v-if="tableMenu" class="tab-stats-thead-menu">Menu</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, key) in trafficDevices"
                    :key="key"
                  >
                    <td>{{ item[0] }}</td>
                    <td>{{ item[1] }}</td>
                    <td>{{ item[5] }}</td>
                    <td>{{ item[3] | currency }}</td>
                    <td>{{ item[4] }}</td>
                    <td class="tab-stats-table-menu-container"
                      v-if="tableMenu"
                    >
                      <button type="button" class="tab-stats-table-menu__button icon--more-menu"
                        @click="toggleMenu"
                      >Menu</button>

                      <div class="tab-stats-table-menu">
                        <ul class="tab-stats-table-menu__list">
                          <li class="tab-stats-table-menu__item">
                            <a class="tab-stats-table-menu__link">Add to Mailchimp</a>
                          </li>
                          <li class="tab-stats-table-menu__item">
                            <a class="tab-stats-table-menu__link">Send Offer</a>
                          </li>
                          <li class="tab-stats-table-menu__item">
                            <a class="tab-stats-table-menu__link">Message</a>
                          </li>
                          <li class="tab-stats-table-menu__item">
                            <a class="tab-stats-table-menu__link">Delete</a>
                          </li>
                          <li class="tab-stats-table-menu__item tab-stats-table-menu__item--unlock">
                            <a class="tab-stats-table-menu__link"><u>Unlock</u> these actions</a>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Traffic Sources -->
        <div class="tab-stats-columns-alt tab-stats-columns-alt--traffic-sources">
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Sources</h3>

            <div class="tab-stats-block tab-stats-block--25" v-if="trafficSourcesSummary !== null">
              <div class="tab-stats__info">
                <p>The best performing traffic source for this product is <strong>{{ trafficSourcesSummary }}</strong> during the selected date range.</p>
              </div>
            </div>

            <!--
            <div class="tab-stats-block tab-stats-block--25">
              <a href="http://www.iseemore.io" class="tab-stats__cta button-outline">Upgrade Now</a>
            </div>
            -->
          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
            <div class="tab-table-container" v-if="trafficSources">
              <table>
                <thead>
                  <tr>
                    <th>Summary</th>
                    <th>Referrer</th>
                    <th>Visitors</th>
                    <th>Sales</th>
                    <th>Conversion Rate</th>
                    <th v-if="tableMenu" class="tab-stats-thead-menu">Menu</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, key) in trafficSources"
                    :key="key"
                  >
                    <td>{{ item[0] }}</td>
                    <td>{{ item[1] }}</td>
                    <td>{{ item[2] }}</td>
                    <td>{{ item[3] | currency }}</td>
                    <td>{{ item[4] }}</td>
                    <td class="tab-stats-table-menu-container"
                      v-if="tableMenu"
                    >
                      <button type="button" class="tab-stats-table-menu__button icon--more-menu"
                        @click="toggleMenu"
                      >Menu</button>

                      <div class="tab-stats-table-menu">
                        <ul class="tab-stats-table-menu__list">
                          <li class="tab-stats-table-menu__item">
                            <a class="tab-stats-table-menu__link">Add to Mailchimp</a>
                          </li>
                          <li class="tab-stats-table-menu__item">
                            <a class="tab-stats-table-menu__link">Send Offer</a>
                          </li>
                          <li class="tab-stats-table-menu__item">
                            <a class="tab-stats-table-menu__link">Message</a>
                          </li>
                          <li class="tab-stats-table-menu__item">
                            <a class="tab-stats-table-menu__link">Delete</a>
                          </li>
                          <li class="tab-stats-table-menu__item tab-stats-table-menu__item--unlock">
                            <a class="tab-stats-table-menu__link"><u>Unlock</u> these actions</a>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Traffic Location -->
        <div class="tab-stats-columns-alt tab-stats-columns-alt--traffic-location">
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Location</h3>

            <div class="tab-stats-block tab-stats-block--25" v-if="trafficLocationSummary !== null">
              <div class="tab-stats__info">
                <p>The most popular shipping destination for this product during the selected date range was <strong>{{ trafficLocationSummary }}</strong> during the selected date range. Consider more localized marketing in this area to further drive revenue.</p>
              </div>
            </div>

            <!--
            <div class="tab-stats-block tab-stats-block--25">
              <a href="http://www.iseemore.io" class="tab-stats__cta button-outline">Upgrade Now</a>
            </div>
            -->
          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
            <div class="tab-table-container" v-if="trafficLocation">
              <table>
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Region</th>
                    <th>City</th>
                    <th>Visitors</th>
                    <th>% of Visitors</th>
                    <th v-if="tableMenu" class="tab-stats-thead-menu">Menu</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, key) in trafficLocation"
                    :key="key"
                  >
                    <td>{{ item[0] }}</td>
                    <td>{{ item[1] }}</td>
                    <td>{{ item[2] }}</td>
                    <td>{{ item[3] }}</td>
                    <td>{{ item[4] }}</td>
                    <td class="tab-stats-table-menu-container"
                      v-if="tableMenu"
                    >
                      <button type="button" class="tab-stats-table-menu__button icon--more-menu"
                        @click="toggleMenu"
                      >Menu</button>

                      <div class="tab-stats-table-menu">
                        <ul class="tab-stats-table-menu__list">
                          <li class="tab-stats-table-menu__item">
                            <a class="tab-stats-table-menu__link">Add to Mailchimp</a>
                          </li>
                          <li class="tab-stats-table-menu__item">
                            <a class="tab-stats-table-menu__link">Send Offer</a>
                          </li>
                          <li class="tab-stats-table-menu__item">
                            <a class="tab-stats-table-menu__link">Message</a>
                          </li>
                          <li class="tab-stats-table-menu__item">
                            <a class="tab-stats-table-menu__link">Delete</a>
                          </li>
                          <li class="tab-stats-table-menu__item tab-stats-table-menu__item--unlock">
                            <a class="tab-stats-table-menu__link"><u>Unlock</u> these actions</a>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Traffic Browser -->
        <div class="tab-stats-columns-alt tab-stats-columns-alt--traffic-browser">
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Browser</h3>

            <div class="tab-stats-block tab-stats-block--25" v-if="trafficBrowserSummary !== null">
              <div class="tab-stats__info">
                <p>Visitors using <strong>{{ trafficBrowserSummary }}</strong> make up the majority of traffic to this product during the selected date range.</p>
              </div>
            </div>

            <!--
            <div class="tab-stats-block tab-stats-block--25">
              <a href="http://www.iseemore.io" class="tab-stats__cta button-outline">Upgrade Now</a>
            </div>
            -->
          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
            <div class="tab-table-container" v-if="trafficBrowser">
              <table>
                <thead>
                  <tr>
                    <th>O/S</th>
                    <th>Browser</th>
                    <th>Browser Version</th>
                    <th>Visitors</th>
                    <th>Sessions</th>
                    <th>% of Visitors</th>
                    <th v-if="tableMenu" class="tab-stats-thead-menu">Menu</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, key) in trafficBrowser"
                    :key="key"
                  >
                    <td>{{ item[0] }}</td>
                    <td>{{ item[1] }}</td>
                    <td>{{ item[2] }}</td>
                    <td>{{ item[3] }}</td>
                    <td>{{ item[4] }}</td>
                    <td>{{ item[5] }}</td>
                    <td class="tab-stats-table-menu-container"
                      v-if="tableMenu"
                    >
                      <button type="button" class="tab-stats-table-menu__button icon--more-menu"
                        @click="toggleMenu"
                      >Menu</button>

                      <div class="tab-stats-table-menu">
                        <ul class="tab-stats-table-menu__list">
                          <li class="tab-stats-table-menu__item">
                            <a class="tab-stats-table-menu__link">Add to Mailchimp</a>
                          </li>
                          <li class="tab-stats-table-menu__item">
                            <a class="tab-stats-table-menu__link">Send Offer</a>
                          </li>
                          <li class="tab-stats-table-menu__item">
                            <a class="tab-stats-table-menu__link">Message</a>
                          </li>
                          <li class="tab-stats-table-menu__item">
                            <a class="tab-stats-table-menu__link">Delete</a>
                          </li>
                          <li class="tab-stats-table-menu__item tab-stats-table-menu__item--unlock">
                            <a class="tab-stats-table-menu__link"><u>Unlock</u> these actions</a>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!--
        <div class="tab-stats-columns-alt tab-stats-columns-alt--traffic-performance">
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Performance</h3>
          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
          </div>
        </div>
        -->

        <div class="tab-stats-columns-alt tab-stats-columns-alt--traffic-map">
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Map</h3>
          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
            <div class="tab-table-container" v-if="trafficMap">
              <div class="tab-table-container-inner" id="tab-table-container-inner--traffic-map">
                <GChart
                  :settings="{ packages: ['corechart'] }"
                  :type="`GeoChart`"
                  :data="trafficMap.geoData"
                  :options="trafficMap.geoOptions"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>

    <!-- Forecast -->
    <template v-if="tabId == 'forecast' && false">
      <section class="tab-stats">
        <h2 class="tab-stats__title">{{ title }}</h2>

        <div class="tab-stats-columns-alt"
          v-if="forecastTotals"
        >
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Sales</h3>

            <div class="tab-stats-block tab-stats-block--25">
              <div class="tab-stats__info">
                <p>Over the next 12 months, Seemore forecasts the following in potential sales.</p>
              </div>
            </div>

            <div class="tab-stats-block tab-stats-block--border-25">
              <div class="tab-stats__info">
                <p><strong>How does Seemore arrive at this forecast?</strong></p>
              </div>
            </div>

            <div class="tab-stats-block tab-stats-block--25">
              <small class="tab-stats__small">Total Sales in last {{ forecastTotals.months }} {{ 'month' | pluralize(forecastTotals.months) }}</small>
              <p class="tab-stats__text tab-stats__text--30">{{ forecastTotals.net_price.amount | currency }}</p>
            </div>

            <div class="tab-stats-block tab-stats-block--25">
              <div class="tab-stats-controller">
                <div class="tab-stats-controller-column tab-stats-controller-column--content">
                  <small class="tab-stats__small">Growth Rate</small>
                  <p class="tab-stats__text tab-stats__text--30">{{ forecastTotals.net_price.growth }}%</p>
                </div>

                <div class="tab-stats-controller-column tab-stats-controller-column--buttons">
                  <button type="button" class="tab-stats-controller__button tab-stats-controller__button--minus"
                    @mousedown="startChangeGrowth('net_price', -.1)"
                    @mouseup="stopChangeGrowth('net_price')"
                  >
                    <span class="hide--visually">Minus</span>
                  </button>

                  <button type="button" class="tab-stats-controller__button tab-stats-controller__button--plus"
                    @mousedown="startChangeGrowth('net_price', .1)"
                    @mouseup="stopChangeGrowth('net_price')"
                  >
                    <span class="hide--visually">Plus</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
            <div class="tab-stats-dashboard" v-if="forecastTotals">
              <ul class="dashboard-tile__list">
                <template v-for="(dashboard, key) in forecastTotals.net_price.dashboards">
                  <dashboard-tile
                    :amount="number(dashboard.amount)"
                    :key="key"
                    :metric="false"
                    :previous-amount="0"
                    :name="dashboard.name"
                    :type="dashboard.type"
                  ></dashboard-tile>
                </template>
              </ul>
            </div>

            <forecast-line-chart
              v-if="this.forecastCharts.net_price"
              :chartData="this.forecastCharts.net_price"
              :options="{responsive: false, maintainAspectRatio: false}"
              :width="729"
              :height="298"
            />
          </div>
        </div>

        <div class="tab-stats-columns-alt"
          v-if="forecastTotals"
        >
          <div class="tab-stats-column-alt tab-stats-column-alt--sidebar tab-stats-column-alt--border">
            <h3 class="tab-stats__subtitle">Volume</h3>

            <div class="tab-stats-block tab-stats-block--25">
              <div class="tab-stats__info">
                <p>Over the next 12 months, Seemore forecasts the following in potential volume.</p>
              </div>
            </div>

            <div class="tab-stats-block tab-stats-block--border-25">
              <div class="tab-stats__info">
                <p><strong>How does Seemore arrive at this forecast?</strong></p>
              </div>
            </div>

            <div class="tab-stats-block tab-stats-block--25">
              <small class="tab-stats__small">Initial Units Sold in last {{ forecastTotals.months }} {{ 'month' | pluralize(forecastTotals.months) }}</small>
              <p class="tab-stats__text tab-stats__text--30">{{ forecastTotals.quantity.amount }}</p>
            </div>

            <div class="tab-stats-block tab-stats-block--25">
              <div class="tab-stats-controller">
                <div class="tab-stats-controller-column tab-stats-controller-column--content">
                  <small class="tab-stats__small">Growth Rate</small>
                  <p class="tab-stats__text tab-stats__text--30">{{ forecastTotals.quantity.growth }}%</p>
                </div>

                <div class="tab-stats-controller-column tab-stats-controller-column--buttons">
                  <button type="button" class="tab-stats-controller__button tab-stats-controller__button--minus"
                    @mousedown="startChangeGrowth('quantity', -.1)"
                    @mouseup="stopChangeGrowth('quantity')"
                  >
                    <span class="hide--visually">Minus</span>
                  </button>

                  <button type="button" class="tab-stats-controller__button tab-stats-controller__button--plus"
                    @mousedown="startChangeGrowth('quantity', .1)"
                    @mouseup="stopChangeGrowth('quantity')"
                  >
                    <span class="hide--visually">Plus</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="tab-stats-column-alt tab-stats-column-alt--main">
            <div class="tab-stats-dashboard" v-if="forecastTotals">
              <ul class="dashboard-tile__list">
                <template v-for="(dashboard, key) in forecastTotals.quantity.dashboards">
                  <dashboard-tile
                    :amount="number(dashboard.amount)"
                    :key="key"
                    :previous-amount="0"
                    :metric="false"
                    :name="dashboard.name"
                    :type="dashboard.type"
                  ></dashboard-tile>
                </template>
              </ul>
            </div>

            <forecast-bar-chart
              v-if="this.forecastCharts.quantity"
              :chartData="this.forecastCharts.quantity"
              :options="{responsive: false, maintainAspectRatio: false}"
              :width="729"
              :height="298"
            />
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
  import _assign from 'lodash/assign'
  import _ceil from 'lodash/ceil'
  import _filter from 'lodash/filter'
  import _find from 'lodash/find'
  import _forEach from 'lodash/forEach'
  import _get from 'lodash/get'
  import _includes from 'lodash/includes'
  import _keyBy from 'lodash/keyBy'
  import _map from 'lodash/map'
  import _maxBy from 'lodash/maxBy'
  import _mean from 'lodash/mean'
  import _orderBy from 'lodash/orderBy'
  import _pick from 'lodash/pick'
  import _reduce from 'lodash/reduce'
  import _round from 'lodash/round'
  import _sortBy from 'lodash/sortBy'
  import _sum from 'lodash/sum'
  import _sumBy from 'lodash/sumBy'
  import moment from 'moment'
  import { GChart } from 'vue-google-charts'

  import DashboardTile from '@/components/DashboardTile'
  import LineChart from '@/components/charts/LineChart'
  import ForecastLineChart from '@/components/charts/ForecastLineChart'
  import ForecastBarChart from '@/components/charts/ForecastBarChart'
  import ProductTile from '@/components/ProductTile'
  import StatBubblesBlock from '@/components/StatBubblesBlock'
  import AnalyticsConnect from '@/components/AnalyticsConnect'
  import ProductDetailVariants from '@/components/ProductDetailVariants'
  import { helpers } from '@/mixins/helpers.js'
  import * as ___ from '@/utils/helpersProductDetailStats.js'

  const filterDashboardItems = (dashboardItemsToFilter, filterByList) => {
    return _filter(dashboardItemsToFilter, (item) => {
      const {
        display_name: displayName,
        name
      } = item

      const nameMatches = _includes(filterByList, name)
      const displayNameMatches = _includes(filterByList, displayName)

      return nameMatches || displayNameMatches
    })
  }

  const sortDashboardItems = (dashboradItemsToSort, sortByList) => {
    return _sortBy(dashboradItemsToSort, (item) => {
      const {
        display_name: displayName,
        name
      } = item

      const nameMatches = sortByList.indexOf(name)
      const displayNameMatches = sortByList.indexOf(displayName)

      return nameMatches || displayNameMatches
    })
  }

  const getSpecificDashboardItems = (dashboardItems, includeList) => {
    let dashboradItems = null

    if (dashboardItems) {
      const itemsToShow = includeList

      // filter
      dashboradItems = filterDashboardItems(dashboardItems, itemsToShow)

      // sort
      dashboradItems = sortDashboardItems(dashboradItems, itemsToShow)
    }

    return dashboradItems
  }

  export default {
    name: 'productDetailStats',
    components: {
      'dashboard-tile': DashboardTile,
      'product-tile': ProductTile,
      'line-chart': LineChart,
      'forecast-line-chart': ForecastLineChart,
      'forecast-bar-chart': ForecastBarChart,
      ProductDetailVariants,
      StatBubblesBlock,
      GChart,
      AnalyticsConnect
    },
    props: {
      id: {
        default: '',
        required: true,
        type: String
      },
      productId: {
        default: '',
        required: true,
        type: String
      },
      tabActiveId: {
        default: '',
        type: String
      },
      tabId: {
        default: '',
        required: true,
        type: String
      },
      title: {
        default: '',
        required: true,
        type: String
      }
    },
    mixins: [helpers],
    mounted () {

    },
    data () {
      return {
        checkoutCart: null,
        checkoutCartPercentage: null,
        checkoutCartVisitors: null,
        checkoutFlowChart: null,
        checkoutFlowPercentage: null,
        checkoutRecovery: null,
        forecastCharts: {
          'net_price': null,
          'quantity': null
        },
        forecastTotals: null,
        growthInterval: false,
        itemsPerPage: 10,
        overviewDashboards: null,
        overviewDifference: null,
        overviewTotalSales: null,
        overviewSalesAmount: null,
        overviewSalesAmountAll: null,
        overviewSalesHistory: null,
        overviewSalesPercentage: null,
        overviewVariants: null,
        product: null,
        sales: null,
        salesAffinity: null,
        salesCustomers: null,
        salesCustomersNew: null,
        salesCustomersReturning: null,
        salesCustomersSummary: null,
        salesCustomersTop: null,
        salesCustomersNewPage: 0,
        salesCustomersReturningPage: 0,
        salesDiscountsCustomersPage: 0,
        salesDiscounts: null,
        salesDiscountsCount: null,
        salesDiscountsCustomers: null,
        salesDiscountsSummary: null,
        salesInventorySellThrough: null,
        salesInventorySoldOut: null,
        salesInventorySummary: 0,
        salesInventoryUnits: null,
        salesInventoryOrdered: null,
        salesInventoryVariants: null,
        salesOrders: null,
        salesOrdersSummary: null,
        salesOrderVelocity: null,
        salesPerformance: null,
        salesPerformanceBest: null,
        salesPerformanceWorst: null,
        salesReturns: null,
        salesReturnsCustomers: null,
        salesReturnsSummary: null,
        tableMenu: false,
        topResults: null,
        trafficBrowser: ___.defaultTrafficBrowser(),
        trafficBrowserSummary: ___.defaultTrafficBrowserSummary(),
        trafficDevices: ___.defaultTrafficDevices(),
        trafficDevicesSummary: ___.defaultTrafficDevicesSummary(),
        trafficLocation: ___.defaultTrafficLocation(),
        trafficLocationSummary: ___.defaultTrafficLocationSummary(),
        trafficMap: ___.defaultTrafficMap(),
        trafficSources: ___.defaultTrafficSources(),
        trafficSourcesSummary: ___.defaultTrafficSourcesSummary(),
        trafficVisitors: ___.defaultTrafficVisitors(),
        trafficVisitorsSummary: ___.defaultTrafficVisitorsSummary()
      }
    },
    computed: {
      salesCustomersNewPages: function () {
        return this.salesCustomersNew.filter((customer, index) => {
          const lower = this.itemsPerPage * this.salesCustomersNewPage
          const upper = this.itemsPerPage * (this.salesCustomersNewPage + 1)
          return index >= lower && index < upper
        })
      },
      salesCustomersReturningPages: function () {
        return this.salesCustomersReturning.filter((customer, index) => {
          const lower = this.itemsPerPage * this.salesCustomersReturningPage
          const upper = this.itemsPerPage * (this.salesCustomersReturningPage + 1)
          return index >= lower && index < upper
        })
      },
      salesDiscountsCustomersPages: function () {
        return this.salesDiscountsCustomers.filter((customer, index) => {
          const lower = this.itemsPerPage * this.salesDiscountsCustomersPage
          const upper = this.itemsPerPage * (this.salesDiscountsCustomersPage + 1)
          return index >= lower && index < upper
        })
      },
      statsGroupOne: function () {
        const itemsToShow = [
          'Total Orders',
          'Gross Sales',
          'Discounts',
          'Returns',
          'Net Sales',
          'Shipping', // shipping is not currently returned from API
          'Tax',
          'Total Sales'
        ]

        return getSpecificDashboardItems(this.overviewDashboards, itemsToShow)
      },
      statsGroupTwo: function () {
        const itemsToShow = [
          'Unique Variants',
          'Units Ordered',
          'Total Discounts',
          'Units Returned',
          'Average Order Value',
          'Average Discount Amount',
          'Average Discount Percentage'
        ]

        return getSpecificDashboardItems(this.overviewDashboards, itemsToShow)
      }
    },
    methods: {
      averageMonthlyGrowth (itemSales, key) {
        let changeRates = _reduce(itemSales.slice(0, itemSales.length - 1), (result, o, i) => {
          let next = itemSales[i + 1][key]
          result.push(((next - o[key]) / o[key]) * 100)
          return result
        }, [])
        return _round(_mean(changeRates) || 0, 2)
      },
      closeTableMenu () {
        // Close table menu on outside click
        document.body.addEventListener('click', (e) => {
          if (!e.target.classList.contains('tab-stats-table-menu__button')) {
            let active = document.querySelector('.tab-stats-table-menu-container.active')
            if (active !== null) {
              active.classList.remove('active')
            }
          }
        })
      },
      incrementGrowth (key, growth) {
        // Incerement forecast group for key
        this.forecastTotals[key].growth = _round(this.forecastTotals[key].growth + growth, 1)
      },
      iScrollUpdate () {
        this.$parent.iScrollUpdate()
      },
      loadCheckout () {
        let carts = this.$parent.checkoutCart
        let cartsAll = this.$parent.checkoutCartAll
        let dashboards = this.$parent.dashboards

        if (cartsAll && carts && dashboards) {
          let cartTotals = carts.totals

          // Checkout Cart
          this.checkoutCart = [
            { name: 'Added to Cart', amount: `${cartTotals.carts.count} Times`, type: 'string' },
            { name: 'Abandoned', amount: `${cartTotals.carts.abandon.count} Times`, type: 'string' },
            { name: 'Lost Revenue', amount: cartTotals.carts.abandon.price, type: 'money' }
          ]

          // Checkout Cart Summary
          let cartPercentage = Math.round((parseInt(cartTotals.carts.count, 10) / parseInt(cartsAll.totals.carts.count, 10) * 100))
          this.checkoutCartPercentage = this.isNumeric(cartPercentage) ? `${cartPercentage}%` : `0%`

          // Checkout Recovery Summary
          this.checkoutCartVisitors = cartTotals.carts.count

          // Checkout Flow Chart
          let orderedCount = _find(dashboards, ['name', 'Total Orders']).amount
          let addedPercentage = Math.round((parseInt(cartTotals.checkouts.count, 10) / parseInt(cartTotals.carts.count, 10) * 100))
          let checkoutPercentage = Math.round((parseInt(orderedCount, 10) / parseInt(cartTotals.checkouts.count, 10) * 100))
          let purchasedPercentage = Math.round((parseInt(orderedCount, 10) / parseInt(cartTotals.carts.count, 10) * 100))

          this.checkoutFlowChart = {
            added: cartTotals.carts.count,
            added_percentage: this.isNumeric(addedPercentage) ? `${addedPercentage}%` : `0%`,
            started: cartTotals.checkouts.count,
            started_percentage: this.isNumeric(checkoutPercentage) ? `${checkoutPercentage}%` : `0%`,
            purchased: orderedCount,
            purchased_percentage: this.isNumeric(purchasedPercentage) ? `${purchasedPercentage}%` : `0%`
          }

          // Checkout Flow Summary
          this.checkoutFlowPercentage = this.isNumeric(purchasedPercentage) ? `${purchasedPercentage}%` : `0%`

          // Checkout Recovery
          const calculateRecoveryEffectiveness = function () {
            const abandonded = cartTotals.checkouts.abandon.count
            const recovered = cartTotals.checkouts.abandon.count
            const recoveredDividedByAbandoned = (recovered / abandonded)

            let recoveryEffectiveness = 0

            if (isNaN(recoveredDividedByAbandoned)) {
              return recoveryEffectiveness
            }

            recoveryEffectiveness = Math.floor((recoveredDividedByAbandoned) * 100)

            return recoveryEffectiveness
          }

          this.checkoutRecovery = [
            { name: 'Abandoned Checkouts', amount: cartTotals.checkouts.abandon.count, type: 'int' },
            { name: 'Checkouts Recovered', amount: cartTotals.checkouts.complete.count, type: 'int' },
            { name: 'Checkouts Recovery Value', amount: cartTotals.checkouts.complete.price, type: 'money' },
            { name: 'Checkout Recovery Effectiveness', amount: calculateRecoveryEffectiveness(), type: 'percent' }
          ]
        } else {
          this.checkoutCart = null
          this.checkoutCartPercentage = null
          this.checkoutCartVisitors = null
          this.checkoutFlowChart = null
          this.checkoutFlowPercentage = null
          this.checkoutRecovery = null
        }
      },
      unitsStocked (variantId) {
        return _sumBy(this.$parent.salesInventory.filter(inv => inv.variant_id === variantId), 'inventory')
      },
      unitsSold (variantId) {
        const sold = this.$parent.variantTopResults.find(variant => variant.id === variantId)
        return sold ? sold.sales.quantity : 0
      },
      variantDeleted (variantId) {
        if (this.product) {
          const variant = this.product.variants.find(variant => variant.id === variantId)
          if (variant) {
            return !!variant.deleted
          }
        }
        return false
      },
      variantSellThroughRate (variantId) {
        const stocked = this.unitsStocked(variantId)
        const sold = this.unitsSold(variantId)

        return (sold / stocked * 100).toFixed(0)
      },
      loadDashboards () {
        let dashboards = this.$parent.dashboards

        if (dashboards) {
          // Overview Dashboards
          let totalSales = _find(dashboards, ['name', 'Total Sales'])
          let difference = this.numberDifference(totalSales.amount, totalSales.previous_amount)

          this.overviewDifference = difference
          this.overviewTotalSales = totalSales
          this.overviewDashboards = dashboards

          // Sales Orders Block
          let averageOrderValue = _find(dashboards, ['name', 'Average Order Value'])
          let totalOrders = _find(dashboards, ['name', 'Total Orders'])
          this.salesOrders = [totalOrders, averageOrderValue]

          // Sales Discounts Block
          let averageDiscountAmount = _find(dashboards, ['name', 'Average Discount Amount'])
          let averageDiscountPercentage = _find(dashboards, ['name', 'Average Discount Percentage'])
          let discounts = _find(dashboards, ['name', 'Discounts'])
          this.salesDiscounts = [
            { amount: discounts.amount, name: 'Total Amount Discounted', previous_amount: discounts.previous_amount, type: discounts.type },
            averageDiscountAmount,
            averageDiscountPercentage
          ]

          // Sales Returns Block
          let amountRefunded = _find(dashboards, ['name', 'Returns'])
          let unitsReturned = _find(dashboards, ['name', 'Units Returned'])
          this.salesReturns = [
            unitsReturned,
            { amount: amountRefunded.amount, name: 'Amount Refunded', previous_amount: amountRefunded.previous_amount, type: amountRefunded.type }
          ]
        } else {
          this.overviewDifference = null
          this.overviewTotalSales = null
          this.overviewDashboards = null
          this.salesOrders = null
          this.salesDiscounts = null
          this.salesReturns = null
        }
      },
      loadDashboardsSummaries () {
        let dashboards = this.$parent.dashboards
        let dashboardsAll = this.$parent.dashboardsAll

        if (dashboardsAll && dashboards) {
          // Overview Summary
          let sales = _find(dashboards, ['name', 'Total Sales'])
          let salesAll = _find(dashboardsAll, ['name', 'Total Sales'])
          let salesPercentage = ((parseInt(sales.amount, 10) / parseInt(salesAll.amount, 10) * 100))
          this.overviewSalesAmount = sales.amount
          this.overviewSalesAmountAll = salesAll.amount
          this.overviewSalesPercentage = this.isNumeric(salesPercentage) ? salesPercentage : 0

          // Sales Orders Summary
          let orders = _find(dashboards, ['name', 'Total Orders'])
          let ordersAll = _find(dashboardsAll, ['name', 'Total Orders'])
          let ordersPercentage = ((parseInt(orders.amount, 10) / parseInt(ordersAll.amount, 10) * 100))
          this.salesOrdersSummary = this.isNumeric(ordersPercentage) ? ordersPercentage : 0

          // Sales Discounts Summary
          let discounts = _find(dashboards, ['name', 'Discounts'])
          let discountsAll = _find(dashboardsAll, ['name', 'Discounts'])
          let discountsPercentage = ((parseInt(discounts.amount, 10) / parseInt(discountsAll.amount, 10) * 100))
          this.salesDiscountsSummary = this.isNumeric(discountsPercentage) ? discountsPercentage : 0

          // Sales Returns Summary
          let returns = _find(dashboards, ['name', 'Returns'])
          let returnsAll = _find(dashboardsAll, ['name', 'Returns'])
          let returnsPercentage = ((parseInt(returns.amount, 10) / parseInt(returnsAll.amount, 10) * 100))
          this.salesReturnsSummary = this.isNumeric(returnsPercentage) ? returnsPercentage : 0
        } else {
          this.overviewSalesAmount = null
          this.overviewSalesAmountAll = null
          this.overviewSalesPercentage = null
          this.salesOrdersSummary = null
          this.salesDiscountsSummary = null
          this.salesReturnsSummary = null
        }
      },
      loadOverviewSales () {
        let sales = this.$parent.sales

        if (sales) {
          // Overview Variants Table
          if (this.product) {
            let variantIds = this.$parent.variantIds
            var variants = this.product.variants

            if (variantIds && variantIds.length > 0) {
              variants = _filter(variants, item => _includes(variantIds, item.id))
            }

            for (let item of variants) {
              let productItem = _find(sales.item_sales, ['variant_id', item.id])

              if (typeof productItem !== 'undefined') {
                item.quantity = productItem.quantity
                item.price = productItem.price
                item.total_discount = productItem.total_discount
                item.refund = productItem.refund
                item.net_price = productItem.net_price
                item.total_tax = productItem.total_tax
                item.total_price = productItem.total_price
              } else {
                item.quantity = 0
                item.price = 0
                item.total_discount = 0
                item.refund = 0
                item.net_price = 0
                item.total_tax = 0
                item.total_price = 0
              }
            }

            this.overviewVariants = variants
          }
        } else {
          this.overviewVariants = null
        }
      },
      loadSales () {
        let sales = this.$parent.sales

        if (sales) {
          // Sales Orders Block - Order Velocity
          let orderVelocity = sales.totals.velocity_seconds
          let orderVelocityTotal = orderVelocity === 0 ? 'N/A' : moment.duration(orderVelocity, 'seconds').humanize()
          this.salesOrderVelocity = { name: 'Order Velocity', amount: orderVelocityTotal }

          // Sales Discounts Block - Discounts
          let discountCount = sales.totals.discount_count
          this.salesDiscountsCount = { name: 'Discounts', amount: discountCount }

          // Sales Performance Block
          if (sales.totals.best_time && sales.totals.best_day && sales.totals.best_month) {
            this.salesPerformance = [
              { name: 'Best Time of Day', amount: sales.totals.best_time },
              { name: 'Best Day of the Week', amount: sales.totals.best_day },
              { name: 'Best Month', amount: sales.totals.best_month }
            ]
          } else {
            this.salesPerformance = [
              { name: 'Best Time of Day', amount: 'N/A' },
              { name: 'Best Day of the Week', amount: 'N/A' },
              { name: 'Best Month', amount: 'N/A' }
            ]
          }
        } else {
          this.salesOrderVelocity = null
          this.salesDiscountsCount = null
          this.salesPerformance = null
        }
      },
      loadSalesAffinity () {
        this.salesAffinity = this.$parent.salesAffinity
      },
      loadSalesChart () {
        let salesHistory = this.$parent.salesHistory
        let salesHistoryPrevious = this.$parent.salesHistoryPrevious

        if (salesHistory && salesHistoryPrevious) {
          let dateRange = this.$parent.$parent.filterDateRange
          let days = Array.from(dateRange.by('day'))
          let salesHistoryKey = _keyBy(salesHistory.item_sales, 'day')
          let dateRangePrevious = this.$parent.previousDateRange
          let daysPrevious = Array.from(dateRangePrevious.by('day'))
          let salesHistoryPreviousKey = _keyBy(salesHistoryPrevious.item_sales, 'day')
          let dateFormat = 'MMM D, YYYY'

          this.overviewSalesHistory = {
            labels: days.map(d => d.format('MMM. D')),
            datasets: [
              {
                data: days.map(d => _get(salesHistoryKey, `${d.format('YYYY-MM-DD')}.net_price`, 0)),
                borderColor: '#999999',
                backgroundColor: '#999999',
                fill: false,
                labels: days.map(d => d.format(dateFormat))
              },
              {
                data: daysPrevious.map(d => _get(salesHistoryPreviousKey, `${d.format('YYYY-MM-DD')}.net_price`, 0)),
                borderColor: '#dddddd',
                backgroundColor: '#dddddd',
                fill: false,
                labels: daysPrevious.map(d => d.format(dateFormat))
              }
            ]
          }
        } else {
          this.overviewSalesHistory = null
        }
      },
      loadSalesCustomerTopResults () {
        let customerTopResults = this.$parent.customerTopResults
        let customerTopResultsAll = this.$parent.customerTopResultsAll
        let dashboards = this.$parent.dashboards

        if (customerTopResults && customerTopResultsAll && dashboards) {
          let customersNew = _filter(customerTopResults, ['orders_count', 1])
          let customersNewCount = Number(customersNew.length)
          let customersReturning = _filter(customerTopResults, obj => obj.orders_count > 1)
          let customersReturningCount = Number(customersReturning.length)
          let customersTotalCount = customersNewCount + customersReturningCount

          // Sales Customers Block
          this.salesCustomers = [
            { name: 'New Customers', amount: customersNewCount },
            { name: 'Returning Customers', amount: customersReturningCount },
            { name: 'Total Customers', amount: customersTotalCount }
          ]

          // Sales Customers Block - New Customer Table
          this.salesCustomersNew = customersNew

          // Sales Customers Block - Returning Customer Table
          this.salesCustomersReturning = customersReturning

          // Sales Customers Summary
          let totalCustomersAmountAll = customerTopResultsAll.length
          let totalCustomersPercentage = ((parseInt(customersTotalCount, 10) / parseInt(totalCustomersAmountAll, 10) * 100))
          this.salesCustomersSummary = this.isNumeric(totalCustomersPercentage) ? totalCustomersPercentage : 0

          // Sales Customers Block - Top Customer Table
          this.salesCustomersTop = customerTopResults

          // Sales Discounts - Customer Discounts Table
          this.salesDiscountsCustomers = _filter(customerTopResults, (obj) => obj.sales.total_discount > 0)

          // Sales Returns - Customer Returns Table
          this.salesReturnsCustomers = _filter(customerTopResults, (obj) => obj.sales.refund > 0)
        } else {
          this.salesCustomers = null
          this.salesCustomersNew = null
          this.salesCustomersReturning = null
          this.salesCustomersSummary = null
          this.salesCustomersTop = null
          this.salesDiscountsCustomers = null
          this.salesReturnsCustomers = null
        }
      },
      loadSalesForecastChartData (itemSales, key, decimals = 2) {
        let nextMonthsRange = moment.range(moment().add(1, 'months'), moment().add(12, 'months'))
        let nextMonths = Array.from(nextMonthsRange.by('month'))
        let last = itemSales.length > 0 ? itemSales.slice(-1)[0][key] : 0
        let growth = this.forecastTotals[key].growth

        this.forecastCharts[key] = {
          labels: nextMonths.map(m => m.format('MMM YYYY')),
          datasets: [
            {
              data: _reduce(Array(13), (results, o, i) => {
                let lastItem = results.length > 0 ? results.slice(-1)[0] : last
                let result = lastItem * (1 + (growth / 100))

                // Round to decimals or round up
                if (decimals > 0) {
                  result = _round(result, decimals)
                } else {
                  result = _ceil(result)
                }

                results.push(result)
                return results
              }, []).slice(-12),
              borderColor: 'rgba(186, 186, 186, 1)',
              backgroundColor: 'rgba(186, 186, 186, .4)'
            }
          ]
        }
      },
      loadSalesForecastCharts (key) {
        let itemSales = _orderBy(this.$parent.salesForecastHistory.item_sales, ['month'])

        if (key === 'net_price') {
          this.loadSalesForecastChartData(itemSales, 'net_price')
          this.forecastTotals.net_price.dashboards = [
            {name: 'Average Sales Per Month', amount: this.forecastTotals.net_price.average, type: 'money'},
            {name: 'Sales Next 12 Months', amount: _sum(this.forecastCharts.net_price.datasets[0].data), type: 'money'}
          ]
        } else {
          this.loadSalesForecastChartData(itemSales, 'quantity', 0)
          this.forecastTotals.quantity.dashboards = [
            {name: 'Average Units Sold Per Month', amount: this.forecastTotals.quantity.average, type: 'int'},
            {name: 'Units Sold Next 12 Months', amount: _sum(this.forecastCharts.quantity.datasets[0].data), type: 'int'}
          ]
        }
      },
      loadSalesForecastHistory () {
        let itemSales = _orderBy(this.$parent.salesForecastHistory.item_sales, ['month'])
        let months = itemSales.length
        let netPrice = _sumBy(itemSales, 'net_price')
        let quantity = _sumBy(itemSales, 'quantity')
        let averageNetGrowth = this.averageMonthlyGrowth(itemSales, 'net_price')
        let averageQuantityGrowth = this.averageMonthlyGrowth(itemSales, 'quantity')

        this.forecastTotals = {
          months: months,
          net_price: {
            amount: netPrice,
            average: netPrice > 0 ? _round(netPrice / months, 2) : 0,
            estimatedGrowth: averageNetGrowth,
            growth: averageNetGrowth
          },
          quantity: {
            amount: quantity,
            average: quantity > 0 ? _round(quantity / months) : 0,
            estimatedGrowth: averageQuantityGrowth,
            growth: averageQuantityGrowth
          }
        }

        this.loadSalesForecastCharts('net_price')
        this.loadSalesForecastCharts('quantity')
      },
      loadSalesInventory () {
        let sales = this.$parent.sales.item_sales
        let salesInventory = this.$parent.salesInventory
        let variantTopResults = this.$parent.variantTopResults

        if (sales && variantTopResults && salesInventory) {
          // Sales Inventory - Dashboard
          let inventorySum = _sumBy(salesInventory, 'inventory')
          this.salesInventoryUnits = { name: 'Total Units Stocked', amount: inventorySum }

          // Sales Inventory Sell Through Rate
          let unitsOrdered = _sumBy(sales, 'quantity')
          this.salesInventoryOrdered = { name: 'Total Units Sold', amount: unitsOrdered }
          let sellThroughRate = Math.round((parseInt(unitsOrdered, 10) / parseInt(inventorySum, 10) * 100))
          let sellThroughRatePercentage = this.isNumeric(sellThroughRate) ? `${sellThroughRate}%` : `0%`
          this.salesInventorySellThrough = { name: 'Sell Through Rate', amount: sellThroughRatePercentage }
        } else {
          this.salesInventoryUnits = null
          this.salesInventorySellThrough = null
          this.salesInventoryOrdered = null
        }
      },
      loadSalesInventorySellouts () {
        let salesInventory = this.$parent.salesInventory

        if (salesInventory) {
          let salesInventorySellouts = _filter(salesInventory, ['sellout', true])
          let variants = this.$parent.variantTopResults

          this.salesInventorySoldOut = _map(salesInventorySellouts, (obj) => {
            return _assign(_pick(_find(variants, { id: obj.variant_id }), 'sku', 'title'), obj)
          })
        } else {
          this.salesInventorySoldOut = null
        }
      },
      loadSalesInventorySummaries () {
        let salesInventory = this.$parent.salesInventory
        let variantTopResults = this.$parent.variantTopResults

        if (salesInventory) {
          // Sales Inventory Summary
          if (salesInventory.length > 0) {
            let inventorySellout = _filter(salesInventory, ['sellout', true])
            let variantIds = this.$parent.variantIds

            if (variantIds && variantIds.length > 0 && variantTopResults) {
              let inventorySelloutVariants = _map(inventorySellout, variantTopResults.id)
              this.salesInventorySummary = inventorySelloutVariants.length
            } else {
              this.salesInventorySummary = inventorySellout.length
            }
          } else {
            this.salesInventorySummary = 0
          }
        }
      },
      loadSalesVariantTopResults () {
        let variantTopResults = this.$parent.variantTopResults

        if (variantTopResults && variantTopResults.length > 0) {
          // Sales Performance
          this.salesPerformanceBest = variantTopResults[0]

          if (variantTopResults.length >= 1) {
            this.salesPerformanceWorst = variantTopResults[variantTopResults.length - 1]
          }

          // Sales Inventory - View Variants
          this.salesInventoryVariants = variantTopResults
        } else {
          this.salesPerformanceBest = null
          this.salesPerformanceWorst = null
          this.salesInventoryVariants = null
        }
      },
      loadTab () {
        if (this.tabId === this.tabActiveId) {
          this.closeTableMenu()
        }
      },
      loadTrafficBrowser () {
        let trafficBrowser = this.$parent.trafficBrowser

        if (trafficBrowser) {
          let dimensions = _find(trafficBrowser, {'field': 'dimensions'})
          let metrics = _find(trafficBrowser, {'field': 'metrics'})
          let totalUsers = _find(metrics.values, {'field': 'total_users'}).value

          if (dimensions.rows && dimensions.rows.length && metrics.values && metrics.values.length) {
            let arrays = _forEach(dimensions.rows, (obj) => {
              let visitorsPercentage = Math.round((parseInt(obj[3], 10) / parseInt(totalUsers, 10) * 100))
              obj[obj.length] = this.isNumeric(visitorsPercentage) ? `${visitorsPercentage}%` : `0%`
              return obj
            })

            // Traffic Browsers
            this.trafficBrowser = arrays

            // Traffic Browsers Summary
            let maxVisitorsObject = _maxBy(dimensions.rows, (obj) => obj[3])
            this.trafficBrowserSummary = `${maxVisitorsObject[1]} on ${maxVisitorsObject[0]}`
          }
        } else {
          this.trafficBrowser = ___.defaultTrafficBrowser() // was null
          this.trafficBrowserSummary = ___.defaultTrafficBrowserSummary() // was null
        }
      },
      loadTrafficDevices () {
        let trafficDevices = this.$parent.trafficDevices

        if (trafficDevices) {
          let dimensions = _find(trafficDevices, {'field': 'dimensions'})
          let metrics = _find(trafficDevices, {'field': 'metrics'})
          let conversionRate = _find(metrics.values, {'field': 'conversion_rate'}).value
          let totalUsers = _find(metrics.values, {'field': 'total_users'}).value

          if (dimensions.rows && dimensions.rows.length && metrics.values && metrics.values.length) {
            let arrays = _forEach(dimensions.rows, (obj) => {
              let conversionPercentage = Math.round((parseInt(obj[3], 10) / parseInt(conversionRate, 10) * 100))
              obj[obj.length] = this.isNumeric(conversionPercentage) ? `${conversionPercentage}%` : `0%`
              let visitorsPercentage = Math.round((parseInt(obj[1], 10) / parseInt(totalUsers, 10) * 100))
              obj[obj.length] = this.isNumeric(visitorsPercentage) ? `${visitorsPercentage}%` : `0%`
              return obj
            })

            // Traffic Devices
            this.trafficDevices = arrays

            // Traffic Devices Summary
            let maxVisitorsObject = _maxBy(dimensions.rows, (obj) => obj[1])
            this.trafficDevicesSummary = `${maxVisitorsObject[0]} devices`
          }
        } else {
          this.trafficDevices = ___.defaultTrafficDevices() // was null
          this.trafficDevicesSummary = ___.defaultTrafficDevicesSummary() // was null
        }
      },
      loadTrafficLocation () {
        let trafficLocation = this.$parent.trafficLocation

        if (trafficLocation) {
          let dimensions = _find(trafficLocation, {'field': 'dimensions'})
          let metrics = _find(trafficLocation, {'field': 'metrics'})
          let totalUsers = _find(metrics.values, {'field': 'total_users'}).value

          if (dimensions.rows && dimensions.rows.length && metrics.values && metrics.values.length) {
            let arrays = _forEach(dimensions.rows, (obj) => {
              let visitorsPercentage = Math.round((parseInt(obj[3], 10) / parseInt(totalUsers, 10) * 100))
              obj[obj.length] = this.isNumeric(visitorsPercentage) ? `${visitorsPercentage}%` : `0%`
              return obj
            })

            // Traffic Location
            this.trafficLocation = arrays

            // Traffic Location Summary
            let maxVisitorsObject = _maxBy(dimensions.rows, (obj) => obj[3])
            this.trafficLocationSummary = `${maxVisitorsObject[0]} / ${maxVisitorsObject[1]} / ${maxVisitorsObject[2]}`
          }
        } else {
          this.trafficLocation = ___.defaultTrafficLocation() // was null
          this.trafficLocationSummary = ___.defaultTrafficLocationSummary() // was null
        }
      },
      loadTrafficMap () {
        const that = this

        const trafficMap = that.$parent.trafficMap

        if (trafficMap) {
          let dimensions = _find(trafficMap, {'field': 'dimensions'})

          if (dimensions.rows && dimensions.rows.length) {
            let regionData = dimensions.rows.map(function (data, index) {
              return [
                data[1], // state name (ie. California)
                {
                  v: data[3], // number of visitors
                  f: `${data[3]}` // number of visitors to string
                }
              ]
            })

            that.trafficMap = {
              geoData: [
                ['State', 'Sessions'],
                ...regionData
              ],
              geoOptions: {
                colorAxis: { colors: ['#c6ece0', '#21ce99'] },
                legend: {
                  numberFormat: '###,###'
                },
                is3D: false,
                region: 'US',
                resolution: 'provinces'
              }
            }
          }
        }
      },
      loadTrafficSources () {
        let trafficSources = this.$parent.trafficSources

        if (trafficSources) {
          let dimensions = _find(trafficSources, {'field': 'dimensions'})
          let metrics = _find(trafficSources, {'field': 'metrics'})
          let conversionRate = _find(metrics.values, {'field': 'conversion_rate'}).value

          if (dimensions.rows && dimensions.rows.length && metrics.values && metrics.values.length) {
            let arrays = _forEach(dimensions.rows, (obj) => {
              let conversionPercentage = Math.round((parseInt(obj[4], 10) / parseInt(conversionRate, 10) * 100))
              obj[4] = this.isNumeric(conversionPercentage) ? `${conversionPercentage}%` : `0%`
              return obj
            })

            // Traffic Sources
            this.trafficSources = arrays

            // Traffic Sources Summary
            let maxVisitorsObject = _maxBy(dimensions.rows, (obj) => obj[2])
            this.trafficSourcesSummary = maxVisitorsObject[0]
          }
        } else {
          this.trafficSources = ___.defaultTrafficSources() // was null
          this.trafficSourcesSummary = ___.defaultTrafficSourcesSummary() // was null
        }
      },
      loadTrafficVisitors () {
        let trafficVisitors = this.$parent.trafficVisitors
        let trafficVisitorsAll = this.$parent.trafficVisitorsAll

        if (trafficVisitors && trafficVisitorsAll) {
          let metrics = _find(trafficVisitors, {'field': 'metrics'})
          let metricsAll = _find(trafficVisitorsAll, {'field': 'metrics'})
          let averageTime = _find(metrics.values, {'field': 'average_time_on_page'}).value
          let conversionRate = _find(metrics.values, {'field': 'transactions'}).value
          let loadSpeed = _find(metrics.values, {'field': 'average_page_load_time'}).value
          let sessions = _find(metrics.values, {'field': 'sessions'}).value
          let totalUsers = _find(metrics.values, {'field': 'total_users'}).value
          let totalUsersAll = _find(metricsAll.values, {'field': 'total_users'}).value

          // Checkout Traffic Visitors
          this.trafficVisitors = [
            { name: 'Visitors', amount: totalUsers, type: 'int' },
            { name: 'Sessions', amount: sessions, type: 'int' },
            { name: 'Average Time on Page', amount: this.secondsToTime(averageTime, 0), type: 'string' },
            { name: 'Conversion Rate', amount: conversionRate, type: 'int' },
            { name: 'Page Load Speed', amount: this.secondsToTime(loadSpeed, 1), type: 'string' }
          ]

          // Checkout Traffic Visitors Summary
          let totalUsersPercentage = Math.round((parseInt(totalUsers, 10) / parseInt(totalUsersAll, 10) * 100))
          this.trafficVisitorsSummary = this.isNumeric(totalUsersPercentage) ? `${totalUsersPercentage}%` : `0%`
        } else {
          this.trafficVisitors = ___.defaultTrafficVisitors() // was null
          this.trafficVisitorsSummary = ___.defaultTrafficVisitorsSummary() // was null
        }
      },
      secondsToTime (x, decPlaces) {
        let hours = Math.floor(x / 3600)
        let minutes = Math.floor((x - (hours * 3600)) / 60)
        var seconds = x - (hours * 3600) - (minutes * 60)
        seconds = +seconds.toFixed(decPlaces) // Only add decimal if needed

        var result = hours !== 0 ? hours + ' hr. ' : ''
        result += minutes !== 0 ? minutes + ' min. ' : ''
        result += seconds + ' sec.'

        return result
      },
      startChangeGrowth (key, growth) {
        // Increment growth while mousedown
        this.incrementGrowth(key, growth)
        if (!this.growthInterval) {
          this.growthInterval = setInterval(() => {
            this.incrementGrowth(key, growth)
          }, 100)
        }
      },
      stopChangeGrowth (key) {
        // Stop incrementing growth and update forecast charts
        clearInterval(this.growthInterval)
        this.growthInterval = false
        this.loadSalesForecastCharts(key)
      },
      toggleMenu (e) {
        let element = e.currentTarget
        let parent = element.parentNode
        parent.classList.toggle('active')
      },
      hasNext (page) {
        return this[page + 'Page'] < this.pageCount(page)
      },
      hasPrev (page) {
        return this[page + 'Page'] > 0
      },
      prevPage (page) {
        if (this.hasPrev(page)) {
          this[page + 'Page'] -= 1
        }
      },
      nextPage (page) {
        if (this.hasNext(page)) {
          this[page + 'Page'] += 1
        }
      },
      setPage (page, pageNumber) {
        this[page + 'page'] = pageNumber
      },
      pageCount (page) {
        return Math.ceil(this[page].length / this.itemsPerPage) - 1
      },
      pageRange (page) {
        const lower = (this[page + 'Page'] * this.itemsPerPage) + 1
        const upper = Math.min(this[page].length, (this[page + 'Page'] + 1) * this.itemsPerPage)
        return [lower, upper]
      }
    },
    created () {
    },
    watch: {
      id: function () {
        this.checkoutCart = null
        this.checkoutCartPercentage = null
        this.checkoutCartVisitors = null
        this.checkoutFlowChart = null
        this.checkoutFlowPercentage = null
        this.checkoutRecovery = null

        this.forecastTotals = null

        this.overviewDashboards = null
        this.overviewDifference = null
        this.overviewTotalSales = null
        this.overviewSalesAmount = null
        this.overviewSalesAmountAll = null
        this.overviewSalesHistory = null
        this.overviewSalesPercentage = null
        this.overviewVariants = null

        this.salesAffinity = null
        this.salesCustomers = null
        this.salesCustomersNew = null
        this.salesCustomersReturning = null
        this.salesCustomersSummary = null
        this.salesCustomersTop = null
        this.salesDiscounts = null
        this.salesDiscountsCount = null
        this.salesDiscountsCustomers = null
        this.salesDiscountsSummary = null
        this.salesInventorySellThrough = null
        this.salesInventorySoldOut = null
        this.salesInventorySummary = 0
        this.salesInventoryUnits = null
        this.salesInventoryOrdered = null
        this.salesInventoryVariants = null
        this.salesOrders = null
        this.salesOrdersSummary = null
        this.salesOrderVelocity = null
        this.salesPerformance = null
        this.salesPerformanceBest = null
        this.salesPerformanceWorst = null
        this.salesReturns = null
        this.salesReturnsCustomers = null
        this.salesReturnsSummary = null

        this.trafficBrowser = ___.defaultTrafficBrowser() // was null
        this.trafficBrowserSummary = ___.defaultTrafficBrowserSummary() // was null
        this.trafficDevices = ___.defaultTrafficDevices() // was null
        this.trafficDevicesSummary = ___.defaultTrafficDevicesSummary() // was null
        this.trafficLocation = ___.defaultTrafficLocation() // was null
        this.trafficLocationSummary = ___.defaultTrafficLocationSummary() // was null
        this.trafficMap = ___.defaultTrafficMap() // was null
        this.trafficSources = ___.defaultTrafficSources() // was null
        this.trafficSourcesSummary = ___.defaultTrafficSourcesSummary() // was null
        this.trafficVisitors = ___.defaultTrafficVisitors() // was null
        this.trafficVisitorsSummary = ___.defaultTrafficVisitorsSummary() // was null
      },
      '$parent.checkoutCart': function () {
        if (this.tabId === this.tabActiveId) {
          this.loadCheckout()
        }
      },
      '$parent.customerTopResultsAll': function () {
        if (this.tabId === this.tabActiveId) {
          this.loadSalesCustomerTopResults()
        }
      },
      '$parent.dashboards': function () {
        if (this.tabId === this.tabActiveId) {
          this.loadDashboards()
        }
      },
      '$parent.dashboardsAll': function () {
        if (this.tabId === this.tabActiveId) {
          this.loadDashboardsSummaries()
        }
      },
      '$parent.product': function (arr) {
        this.product = arr
      },
      '$parent.sales': function () {
        if (this.tabId === this.tabActiveId && this.tabId === 'overvie') {
          this.loadOverviewSales()
        }
        if (this.tabId === this.tabActiveId && this.tabId === 'sales') {
          this.loadSales()
        }
      },
      '$parent.salesAffinity': function () {
        if (this.tabId === this.tabActiveId) {
          this.loadSalesAffinity()
        }
      },
      '$parent.salesHistoryPrevious': function () {
        if (this.tabId === this.tabActiveId) {
          this.loadSalesChart()
        }
      },
      '$parent.salesForecastHistory': function () {
        if (this.tabId === 'forecast') {
          this.loadSalesForecastHistory()
        }
      },
      '$parent.salesInventory': function () {
        if (this.tabId === this.tabActiveId) {
          this.loadSalesInventorySellouts()
          this.loadSalesInventorySummaries()
        }
      },
      '$parent.trafficBrowser': function () {
        if (this.tabId === this.tabActiveId) {
          this.loadTrafficBrowser()
        }
      },
      '$parent.trafficDevices': function () {
        if (this.tabId === this.tabActiveId) {
          this.loadTrafficDevices()
        }
      },
      '$parent.trafficLocation': function () {
        if (this.tabId === this.tabActiveId) {
          this.loadTrafficLocation()
        }
      },
      '$parent.trafficMap': function () {
        if (this.tabId === this.tabActiveId) {
          this.loadTrafficMap()
        }
      },
      '$parent.trafficSources': function () {
        if (this.tabId === this.tabActiveId) {
          this.loadTrafficSources()
        }
      },
      '$parent.trafficVisitors': function () {
        if (this.tabId === this.tabActiveId) {
          this.loadTrafficVisitors()
        }
      },
      '$parent.variantTopResults': function () {
        if (this.tabId === this.tabActiveId) {
          this.loadSalesVariantTopResults()
          this.loadSalesInventory()
          this.loadSalesInventorySellouts()
        }
      },
      tabActiveId: function () {
        this.loadTab()
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/js/components/ProductDetailStats.vue